import api from "@/api";
import store from "@/store";

export default class Route {
  constructor(data) {
    this.id = data.id;
    this.name = data.name || null;
    this.description = data.description || null;
    this.coordinatesIds =
      data.coordinatesIds ||
      data.points?.map((point) => point.coordinate.id) ||
      [];
    this.order = data.order ?? Infinity;

    this.isNew = data.isNew || false;
    this.isCustom = data.isCustom || false;

    this.isEditing = false;
    this.savedEditingCoordinatesIds = null;

    this.geometry = null;
  }

  get coordinates() {
    return this.coordinatesIds
      .map((id) => {
        return store.getters["Coordinates/getGlobalCoordinateById"](id);
      })
      .filter((coordinate) => coordinate !== null);
  }
  get isValid() {
    return this.coordinates.length > 1;
  }

  get coordinatesYears() {
    return this.coordinates
      .map((coordinate) => coordinate.allImagesYears)
      .flat()
      .filter((year) => year !== null && year !== 0);
  }
  get fromYear() {
    return Math.min(...this.coordinatesYears);
  }
  get toYear() {
    return Math.max(...this.coordinatesYears);
  }
  get label() {
    if (this.name) return this.name;

    const firstStreet = this.coordinates.at(0)?.street?.name || "";
    const lastStreet = this.coordinates.at(-1)?.street?.name || "";

    const label = [firstStreet, lastStreet].filter(Boolean).join(" ");

    return label || "Unbenannte Route";
  }

  addCoordinate(coordinateId) {
    this.coordinatesIds.push(coordinateId);

    this._actualizeGeometry();
  }
  removeCoordinate(coordinateId) {
    this.coordinatesIds = this.coordinatesIds.filter(
      (id) => id !== coordinateId
    );

    this._actualizeGeometry();
  }
  setCoordinatesIds(coordinatesIds, isReset = false) {
    this.coordinatesIds = coordinatesIds;
    if (isReset) {
      this.isCustom = false;
      this.savedEditingCoordinatesIds = null;
      this.isEditing = false;
    }

    this._actualizeGeometry();
  }

  startEditing() {
    this.savedEditingCoordinatesIds = [...this.coordinatesIds];
    this.isEditing = true;
  }
  saveEditing() {
    this.savedEditingCoordinatesIds = null;
    this.isEditing = false;

    if (!this.isNew) {
      this.isCustom = true;
    }

    this._actualizeGeometry();
  }
  cancelEditing() {
    this.coordinatesIds = this.savedEditingCoordinatesIds
      ? [...this.savedEditingCoordinatesIds]
      : [];
    this.savedEditingCoordinatesIds = null;
    this.isEditing = false;

    this._actualizeGeometry();
  }

  async _actualizeGeometry() {
    if (this.geometry !== null) {
      this._loadGeometry();
    }
  }

  async getGeometry() {
    if (!this.geometry) {
      await this._loadGeometry();
    }

    return this.geometry;
  }

  async _loadGeometry() {
    if (this.coordinates.length < 2) return false;
    
    try {
      const { data } = await api.getMapboxRoute(this.coordinates);
      
      if (data?.routes?.[0]?.geometry) {
        // Get the route geometry from Mapbox
        const routeGeometry = data.routes[0].geometry;
        const waypoints = data.waypoints || [];
        
        // Check if we need to extend lines to actual coordinates
        // Mapbox snaps coordinates to nearest walkable paths
        const extendedCoordinates = [];
        
        // Add line from first actual coordinate to first waypoint (snapped point)
        if (waypoints[0] && this.coordinates[0]) {
          const actualStart = [this.coordinates[0].lng, this.coordinates[0].lat];
          
          // Only add extension if snap distance is significant (> 5 meters)
          const snapDistance = waypoints[0].distance || 0;
          if (snapDistance > 5) {
            extendedCoordinates.push(actualStart);
          }
        }
        
        // Add all route coordinates
        extendedCoordinates.push(...routeGeometry.coordinates);
        
        // Add line from last waypoint (snapped point) to last actual coordinate
        const lastIndex = waypoints.length - 1;
        if (waypoints[lastIndex] && this.coordinates[this.coordinates.length - 1]) {
          const actualEnd = [
            this.coordinates[this.coordinates.length - 1].lng,
            this.coordinates[this.coordinates.length - 1].lat
          ];
          
          // Only add extension if snap distance is significant (> 5 meters)
          const snapDistance = waypoints[lastIndex].distance || 0;
          if (snapDistance > 5) {
            extendedCoordinates.push(actualEnd);
          }
        }
        
        // Create extended geometry
        this.geometry = {
          type: 'LineString',
          coordinates: extendedCoordinates
        };
      } else {
        this.geometry = null;
      }
    } catch (error) {
      console.error('Error loading route geometry:', error);
      this.geometry = null;
    }
  }
}